import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  CarouselViewport,
  CarouselContainer,
  CarouselSlide,
  CarouselInner,
  PrevButton,
  NextButton,
} from "./styles";

/**
 * Resolves itemsToShow from a breakPoints array based on current window width.
 * @param {Array<{width: number, itemsToShow: number}>} breakPoints
 * @param {number} defaultItemsToShow
 * @returns {number}
 */
function resolveItemsToShow(breakPoints, defaultItemsToShow) {
  if (!breakPoints || breakPoints.length === 0) return defaultItemsToShow;
  let result = defaultItemsToShow;
  for (const bp of breakPoints) {
    if (window.innerWidth >= bp.width) {
      result = bp.itemsToShow;
    }
  }
  return result;
}

/**
 * Drop-in replacement for react-elastic-carousel using embla-carousel-react.
 */
const Carousel = ({
  breakPoints,
  itemsToShow = 1,
  pagination: _pagination, // API compat only, not rendered
  transitionMs = 500,
  children,
}) => {
  const [activeItemsToShow, setActiveItemsToShow] = useState(() =>
    resolveItemsToShow(breakPoints, itemsToShow)
  );

  const emblaOptions = {
    loop: false,
    // embla duration unit: default 25 ≈ 300ms
    duration: Math.min(Math.round(transitionMs / 50), 50),
    // scroll one full "page" per click, matching react-elastic-carousel default
    slidesToScroll: Math.floor(activeItemsToShow) || 1,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Re-calculate itemsToShow on resize and reInit embla with updated slidesToScroll
  useEffect(() => {
    const handleResize = () => {
      const next = resolveItemsToShow(breakPoints, itemsToShow);
      setActiveItemsToShow(next);
      if (emblaApi) {
        emblaApi.reInit({
          slidesToScroll: Math.floor(next) || 1,
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakPoints, itemsToShow, emblaApi]);

  const slideWidth = `calc(100% / ${activeItemsToShow})`;

  return (
    <CarouselInner>
      <CarouselViewport ref={emblaRef}>
        <CarouselContainer>
          {React.Children.map(children, (child, i) => (
            <CarouselSlide key={i} style={{ flex: `0 0 ${slideWidth}`, minWidth: slideWidth }}>
              {child}
            </CarouselSlide>
          ))}
        </CarouselContainer>
      </CarouselViewport>

      <PrevButton onClick={scrollPrev} disabled={!canScrollPrev} aria-label="Previous">
        &#8249;
      </PrevButton>
      <NextButton onClick={scrollNext} disabled={!canScrollNext} aria-label="Next">
        &#8250;
      </NextButton>
    </CarouselInner>
  );
};

export default Carousel;
